import { OrgSidebarConfig } from 'apis/organizationStorage';

import {
  SidebarCardKey,
  SidebarCardType,
} from '../../conversationSidebar.types';
import { cardKey } from '../../utils/cardKey';
import { mergeOrder } from '../../utils/reorder';

export type MergeableCard = {
  readonly cardType: SidebarCardType;
  readonly cardId: string;
  /** Natural fallback position when neither org nor user configured the card. */
  readonly defaultIndex: number;
};

export type MergeSidebarCardsArgs<T extends MergeableCard> = {
  readonly cards: readonly T[];
  readonly orgConfig: OrgSidebarConfig | undefined;
  /** `sidebarCardOrder` — user's ordered card keys. */
  readonly userOrder: readonly string[] | undefined;
  /** `sidebarVisibilityOverrides` — user's per-card show/hide overrides. */
  readonly userVisibility: Readonly<Record<string, boolean>> | undefined;
  /**
   * Returns `false` when a card's org render conditions do not match the
   * current conversation (i.e. the card should be hidden by conditions).
   */
  readonly conditionMatches: (key: SidebarCardKey) => boolean;
  /**
   * Whether agents may personalize when the org config hasn't set it. Must
   * match the settings page default (the legacy customization flag) so the live
   * sidebar and admin view agree on whether overrides apply.
   */
  readonly personalizationDefault: boolean;
};

export type MergeSidebarCardsResult<T extends MergeableCard> = {
  /** All cards in effective order (hidden included). */
  readonly orderedCards: readonly (T & { readonly key: SidebarCardKey })[];
  /** Ordered card keys — persist this when the user reorders. */
  readonly orderedKeys: readonly SidebarCardKey[];
  /** Keys that are hidden for any reason (org, user override, or conditions). */
  readonly hiddenKeys: ReadonlySet<SidebarCardKey>;
  /** Keys hidden specifically because their conditions did not match. */
  readonly conditionHiddenKeys: ReadonlySet<SidebarCardKey>;
  /**
   * Keys hidden by the org default or the agent's own override, ignoring
   * conditions. This is what the per-card visibility toggle reflects — a
   * condition-hidden card can still be shown/hidden here as a personal choice.
   */
  readonly visibilityHiddenKeys: ReadonlySet<SidebarCardKey>;
  /** Whether agents may personalize (org toggle). */
  readonly allowPersonalization: boolean;
};

/**
 * Pure merge of the org default sidebar configuration with the agent's personal
 * overrides for the flat `sidebar-cards-admin-management` model.
 *
 * Order precedence: user order (when personalization is allowed) wins, then the
 * org order, then each card's natural `defaultIndex`. Cards missing from a layer
 * fall through to the next.
 *
 * Visibility precedence: a card whose conditions do not match is always hidden;
 * otherwise the user override wins (when personalization is allowed), then the
 * org default, then visible.
 */
export const mergeSidebarCards = <T extends MergeableCard>({
  cards,
  orgConfig,
  userOrder,
  userVisibility,
  conditionMatches,
  personalizationDefault,
}: MergeSidebarCardsArgs<T>): MergeSidebarCardsResult<T> => {
  const allowPersonalization =
    orgConfig?.allowAgentPersonalization ?? personalizationDefault;

  const cardsWithKey = cards.map((card) => ({ ...card, key: cardKey(card) }));
  const cardsByKey = new Map<string, (typeof cardsWithKey)[number]>(
    cardsWithKey.map((card) => [card.key, card]),
  );

  const orgByKey = new Map(
    (orgConfig?.cards ?? []).map((entry) => [cardKey(entry), entry]),
  );

  // Base order: org-configured cards (that still exist) by their `order`, then
  // any remaining cards by their natural `defaultIndex`. Ties break on key for
  // determinism.
  const baseOrderedKeys = [...cardsWithKey]
    .sort((a, b) => {
      const orderA = orgByKey.get(a.key)?.order ?? Infinity;
      const orderB = orgByKey.get(b.key)?.order ?? Infinity;
      if (orderA !== orderB) return orderA - orderB;
      if (a.defaultIndex !== b.defaultIndex)
        return a.defaultIndex - b.defaultIndex;
      return a.key.localeCompare(b.key);
    })
    .map((card) => card.key);

  // Overlay the user's order when allowed, keeping only keys that still exist
  // and appending any cards the user hasn't positioned yet.
  const orderedKeys = (
    allowPersonalization && userOrder
      ? mergeOrder(
          userOrder.filter((key): key is SidebarCardKey => cardsByKey.has(key)),
          baseOrderedKeys,
        )
      : baseOrderedKeys
  ) as SidebarCardKey[];

  const conditionHiddenKeys = new Set<SidebarCardKey>();
  const visibilityHiddenKeys = new Set<SidebarCardKey>();
  const hiddenKeys = new Set<SidebarCardKey>();

  orderedKeys.forEach((key) => {
    // Evaluate the two hide reasons independently so the visibility toggle can
    // reflect the agent's own choice even when conditions also hide the card.
    const conditionHidden = !conditionMatches(key);
    if (conditionHidden) {
      conditionHiddenKeys.add(key);
    }

    const userOverride = allowPersonalization
      ? userVisibility?.[key]
      : undefined;
    const visibilityHidden =
      userOverride !== undefined
        ? !userOverride
        : (orgByKey.get(key)?.hidden ?? false);
    if (visibilityHidden) {
      visibilityHiddenKeys.add(key);
    }

    if (conditionHidden || visibilityHidden) {
      hiddenKeys.add(key);
    }
  });

  const orderedCards = orderedKeys
    .map((key) => cardsByKey.get(key))
    .filter((card): card is T & { key: SidebarCardKey } => card !== undefined);

  return {
    orderedCards,
    orderedKeys,
    hiddenKeys,
    conditionHiddenKeys,
    visibilityHiddenKeys,
    allowPersonalization,
  };
};
